import { makeObservable, observable, action } from "mobx"
import { 
  isLogicalExpression, 
  isSetComparisonOperator,
  isRangeComparisonOperator,
  isUnaryComparisonOperator 
} from "./types2";

export class BuilderState {
  expression: any;
  constructor() {
    this.expression = '';
    makeObservable(this, {
      expression: observable,
      setExpression: action,
      updateOperator: action,
      updateExpression:action,
      addOperand: action,
  })}
  setExpression(expression: any) {
    this.expression = expression;
 }
 resetExpression() {
  this.expression = '';
 }
 getExpression() {
    return this.expression;
   }
   updateOperator(path: string[], newOperator: string) {
    console.log('Updating operator:', path, newOperator);
    try {
      // First update the operator
      this.expression = this.updateOperatorHelper(this.expression, path, newOperator);

      // Get the current expression at path
      const currentExpr = this.getExpressionAtPath(path);

      // Only handle special cases for comparison operators
      if (isSetComparisonOperator(newOperator) || 
          isRangeComparisonOperator(newOperator) || 
          isUnaryComparisonOperator(newOperator)) {
        
        // Handle comparison operator changes...
        // ... existing comparison operator logic ...

      } 
      // For arithmetic operators, we don't need to modify the structure
      // Just let the operator update happen without additional changes
      
    } catch (error) {
      console.error('Error updating operator:', error);
    }
  }

  getExpressionAtPath(path: string[]): any {
    let current = this.expression;
    for (const key of path) {
      if (current && typeof current === 'object') {
        current = current[key];
      }
    }
    return current;
  }

  private getExpressionAtPathInternal(path: string[]): any {
    let current = this.expression;
    for (const key of path) {
      if (current && typeof current === 'object') {
        current = current[key];
      }
    }
    return current;
  }

  addOperand(path: string[],obj:any) {
    console.log("operand path",path)

    //this.expression.operands.push(obj)
    this.expression = this.addOperandHelper(this.expression, path,obj);
  }

  
  addOperandHelper(expression: any, path: string[], newOperand: any): any {
    if (!Array.isArray(path) || path.length === 0) {
      throw new Error("Invalid path: Path must be a non-empty array");
    }
  
    // Create a copy to ensure immutability
    const updatedExpression = { ...expression };
    let current = updatedExpression;
  
    // Traverse the path except for the last step
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
  
      if (key === "operand") {
        if (!current.operand || typeof current.operand !== "object") {
          throw new Error(`Invalid path: 'operand' must be an object at path index ${i}`);
        }
        current = current.operand;
      } else if (key === "operands") {
        if (!Array.isArray(current.operands)) {
          throw new Error(`Invalid path: 'operands' is not an array at path index ${i}`);
        }
        current = current.operands;
      } else if (!isNaN(Number(key))) {
        const index = Number(key);
        if (!Array.isArray(current) || index < 0 || index >= current.length) {
          throw new Error(`Invalid path: Index ${index} is out of bounds at path index ${i}`);
        }
        current = current[index];
      } else {
        throw new Error(`Invalid path: Unknown key '${key}' at path index ${i}`);
      }
    }
  
    // Handle the final key
    const finalKey = path[path.length - 1];
  
    if (finalKey === "operands") {
      if (!current.operands) {
        current.operands = []; // Initialize if missing
      } else if (!Array.isArray(current.operands)) {
        throw new Error("Invalid path: Final 'operands' is not an array");
      }
      current.operands.push(newOperand); // Add the new operand
    } else {
      throw new Error(`Invalid path: Final key '${finalKey}' must be 'operands'`);
    }
  
    return updatedExpression;
  }
  
  
  


  updateExpression(path: string[], newValue: any | any[]) {
    this.expression = this.updateExpressionHelper(this.expression, path, newValue);
  }

  private updateExpressionHelper(expr: any, path: string[], newValue: any): any {
    if (path.length === 0) {
      return newValue;
    }

    const [current, ...rest] = path;
    
    if (Array.isArray(expr)) {
      const index = parseInt(current);
      return [
        ...expr.slice(0, index),
        this.updateExpressionHelper(expr[index], rest, newValue),
        ...expr.slice(index + 1)
      ];
    } else if (typeof expr === 'object' && expr !== null) {
      return {
        ...expr,
        [current]: this.updateExpressionHelper(expr[current], rest, newValue)
      };
    } else {
      // If we're trying to update a non-object, non-array value, just return the new value
      return newValue;
    }
  }

  addLogicalExpression(operator:string): any {
    this.expression = { operator: operator, operands: [] };
  }

  addLiteralExpression(literalObj:any): any {
    this.expression = literalObj;
  }

  addRootExpression(rootObj:any): any {
    this.expression = rootObj;
  }

  updateDataType(path: string[], newValue: string): any {
    this.expression = this.updateDataTypeHelper(this.expression, path, newValue);
  }


  updateDataTypeHelper(expression: any, path: string[], newValue: string): any {
    if (!Array.isArray(path) || path.length === 0) {
      throw new Error("Invalid path: Path must be a non-empty array");
    }
  
    // Create a deep copy to maintain immutability
    const updatedExpression = JSON.parse(JSON.stringify(expression));
    let current = updatedExpression;
  
    // Traverse the path
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
  
      if (typeof current !== "object" || current === null || !(key in current)) {
        throw new Error(`Invalid path: '${key}' not found at path index ${i}`);
      }
  
      current = current[key];
    }
  
    // Final key to update
    const finalKey = path[path.length - 1];
  
    if (finalKey !== "dataType") {
      throw new Error(`Invalid path: Final key must be 'dataType', but got '${finalKey}'`);
    }
  
    if (typeof current !== "object" || current === null) {
      throw new Error("Invalid path: Target object is not valid");
    }
  
    // Update the dataType value
    current[finalKey] = newValue;
  
    return updatedExpression;
  }
  




  updateRightOperand(path: string[], targetKey: "rightOperand" | "rightOperands",defaultOperandObject: any) {
    this.expression = this.updateRightOperandHelper(this.expression, path, targetKey,defaultOperandObject);
  }
  


 

  private updateRightOperandHelperOld(expr: any, path: string[], targetKey: "rightOperand" | "rightOperands"): any {
    if (path.length === 0) {
      if (expr && typeof expr === "object") {
        // Handle the case for `rightOperands`
        if (targetKey === "rightOperands") {
          if ("rightOperand" in expr) {
            // Convert `rightOperand` to `rightOperands`
            const { rightOperand, ...rest } = expr;
            return {
              ...rest,
              rightOperands: Array.isArray(rightOperand) ? rightOperand : [rightOperand],
            };
          } else if ("rightOperands" in expr) {
            // Keep `rightOperands` as is
            return expr;
          }
        }
  
        // Handle the case for `rightOperand`
        if (targetKey === "rightOperand") {
          if ("rightOperands" in expr) {
            // Convert `rightOperands` to `rightOperand`
            const { rightOperands, ...rest } = expr;
            return {
              ...rest,
              rightOperand: Array.isArray(rightOperands) ? rightOperands[0] : null,
            };
          } else if ("rightOperand" in expr) {
            // Keep `rightOperand` as is
            return expr;
          }
        }
      }
      throw new Error("Invalid expression structure for updating operand");
    }
  
    const [current, ...rest] = path;
  
    if (Array.isArray(expr) && !isNaN(parseInt(current))) {
      const index = parseInt(current);
      if (index >= expr.length) {
        throw new Error("Invalid path: index out of bounds");
      }
      const updatedItem = this.updateRightOperandHelperOld(expr[index], rest, targetKey);
      return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
    } else if (typeof expr === "object" && expr !== null && current in expr) {
      // Update nested property in object
      return {
        ...expr,
        [current]: this.updateRightOperandHelperOld(expr[current], rest, targetKey),
      };
    } else {
      throw new Error("Invalid path or expression type");
    }
  }

  private updateRightOperandHelper(expr: any, path: string[], operandKey: string, obj: any): any {
    if (path.length === 0) {
      if (expr && typeof expr === "object") {
        // Check if `operand` exists and no `leftOperand`
        if ("operand" in expr && !("leftOperand" in expr)) {
          // Rename `operand` to `leftOperand`
          expr = {
            ...expr,
            leftOperand: expr.operand,
          };
          delete expr.operand;
        }
  
        // Ensure the specified operandKey (`rightOperands` or `rightOperand`) exists
        if (operandKey === "rightOperands" && !Array.isArray(expr.rightOperands)) {
          expr.rightOperands = []; // Initialize as an empty array
        } else if (operandKey === "rightOperand" && !("rightOperand" in expr)) {
          expr.rightOperand = null; // Initialize as null or appropriate default
        }
  
        // Add the given object to `rightOperands` if the key is `rightOperands`
        if (operandKey === "rightOperands" && Array.isArray(expr.rightOperands)) {
          if(expr.rightOperands.length === 0)
              expr.rightOperands.push(obj);
        } else if (operandKey === "rightOperand") {
          // Overwrite the single `rightOperand` if key is `rightOperand`
          expr.rightOperand = obj;
        }
  
        return expr;
      }
  
      throw new Error("Invalid expression structure for updating right operand");
    }
  
    const [current, ...rest] = path;
  
    if (Array.isArray(expr) && !isNaN(parseInt(current))) {
      const index = parseInt(current);
      if (index >= expr.length) {
        throw new Error("Invalid path: index out of bounds");
      }
      const updatedItem = this.updateRightOperandHelper(expr[index], rest, operandKey, obj);
      return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
    } else if (typeof expr === "object" && expr !== null && current in expr) {
      // Update nested property in object
      return {
        ...expr,
        [current]: this.updateRightOperandHelper(expr[current], rest, operandKey, obj),
      };
    } else {
      throw new Error("Invalid path or expression type");
    }
  }
  
// add fxArgs
addFxArgs(path: string[], obj: any) {
  this.expression = this.addFxArgsHelper(this.expression, path, obj);
}

private addFxArgsHelper(expr: any, path: string[], obj: any): any {
  if (path.length === 0) {
    if (expr && typeof expr === "object") {
      // Ensure `fxArgs` exists and is an array
      if (!Array.isArray(expr.fxArgs)) {
        expr.fxArgs = [];
      }

      // Push the new object to `fxArgs`
      expr.fxArgs.push(obj);
      
      return expr;
    }

    throw new Error("Invalid expression structure for adding function argument");
  }

  const [current, ...rest] = path;

  if (Array.isArray(expr) && !isNaN(parseInt(current))) {
    const index = parseInt(current);
    if (index >= expr.length) {
      throw new Error("Invalid path: index out of bounds");
    }
    const updatedItem = this.addFxArgsHelper(expr[index], rest, obj);
    return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
  } else if (typeof expr === "object" && expr !== null && current in expr) {
    // Update nested property in object
    return {
      ...expr,
      [current]: this.addFxArgsHelper(expr[current], rest, obj),
    };
  } else {
    throw new Error("Invalid path or expression type");
  }
}

removeFxArgs(path: string[], index: number) {
  this.expression = this.removeFxArgsHelper(this.expression, path, index);
}

private removeFxArgsHelper(expr: any, path: string[], index: number): any {
  if (path.length === 0) {
    if (expr && typeof expr === "object" && Array.isArray(expr.fxArgs)) {
      if (index < 0 || index >= expr.fxArgs.length) {
        throw new Error("Invalid index: out of bounds");
      }

      // Remove the item at the given index
      const updatedFxArgs = [...expr.fxArgs.slice(0, index), ...expr.fxArgs.slice(index + 1)];

      // Ensure the updated object is correctly structured
      if (updatedFxArgs.length === 0) {
        const { fxArgs, ...rest } = expr; // Remove `fxArgs` from object if empty
        return rest;
      }

      return { ...expr, fxArgs: updatedFxArgs };
    }

    throw new Error("Invalid expression structure for removing function argument");
  }

  const [current, ...rest] = path;

  if (Array.isArray(expr) && !isNaN(parseInt(current))) {
    const index = parseInt(current);
    if (index >= expr.length) {
      throw new Error("Invalid path: index out of bounds");
    }
    return [
      ...expr.slice(0, index),
      this.removeFxArgsHelper(expr[index], rest, index),
      ...expr.slice(index + 1),
    ];
  } else if (typeof expr === "object" && expr !== null && current in expr) {
    return {
      ...expr,
      [current]: this.removeFxArgsHelper(expr[current], rest, index),
    };
  } else {
    throw new Error("Invalid path or expression type");
  }
}






  // for set comparison expressions
  addRightOperand(path: string[], newOperand: any) {
    this.expression = this.addRightOperandHelper(this.expression, path, newOperand);
  }
  
  private addRightOperandHelper(expr: any, path: string[], newOperand: any): any {
    if (path.length === 0) {
      if (expr && typeof expr === "object") {
        if ("rightOperands" in expr) {
          // Push to existing `rightOperands` array
          return {
            ...expr,
            rightOperands: [...expr.rightOperands, newOperand],
          };
        } else if ("rightOperand" in expr) {
          // Convert `rightOperand` to `rightOperands` and add the new operand
          const { rightOperand, ...rest } = expr;
          return {
            ...rest,
            rightOperands: [rightOperand, newOperand],
          };
        } else {
          // Add `rightOperands` if neither exists
          return {
            ...expr,
            rightOperands: [newOperand],
          };
        }
      }
      throw new Error("Invalid expression structure for adding right operand");
    }
  
    const [current, ...rest] = path;
  
    if (Array.isArray(expr) && !isNaN(parseInt(current))) {
      const index = parseInt(current);
      if (index >= expr.length) {
        throw new Error("Invalid path: index out of bounds");
      }
      const updatedItem = this.addRightOperandHelper(expr[index], rest, newOperand);
      return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
    } else if (typeof expr === "object" && expr !== null && current in expr) {
      // Update nested property in object
      return {
        ...expr,
        [current]: this.addRightOperandHelper(expr[current], rest, newOperand),
      };
    } else {
      throw new Error("Invalid path or expression type");
    }
  }
  
  removeRightOperand(path: string[], operandIndex: number) {
    this.expression = this.removeRightOperandHelper(this.expression, path, operandIndex);
  }

  removeOperand(path: string[], operandIndex: number) {
    this.expression = this.removeOperandHelper(this.expression, path, operandIndex);
  }

  addCWTBranchOperand(path:string[],cwtBranchObject:any){
      this.addCWTBranchOperandHelper(this.expression,path,cwtBranchObject);return
  }



addCWTBranchOperandHelper(expression: any, path: string[], newOperand: any): any {
    if (!Array.isArray(path) || path.length === 0) {
      throw new Error("Invalid path: Path must be a non-empty array");
    }
  
    if (!newOperand || typeof newOperand !== "object") {
      throw new Error("Invalid newOperand: Must be a non-null object");
    }
  
    // Create a deep copy of the expression for immutability
    const updatedExpression = { ...expression };
    let current = updatedExpression;
  
    // Traverse the path except for the last step
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
  
      if (key === "branchOperand") {
        if (!Array.isArray(current.branchOperand)) {
          throw new Error(`Invalid path: 'branchOperand' is not an array at path index ${i}`);
        }
        current = current.branchOperand;
      } else if (!isNaN(Number(key))) {
        const index = Number(key);
  
        if (index < 0 || index >= current.length) {
          throw new Error(`Invalid path: Index ${index} is out of bounds at path index ${i}`);
        }
        current = current[index];
      } else {
        if (!(key in current)) {
          throw new Error(`Invalid path: Unknown key '${key}' at path index ${i}`);
        }
        current = current[key];
      }
    }
  
    // Handle the final key
    const finalKey = path[path.length - 1];
  
    if (finalKey === "branchOperand") {
      if (!Array.isArray(current.branchOperand)) {
        throw new Error("Invalid path: 'branchOperand' is not an array");
      }
  
      // Add the new branch operand
      current.branchOperand = [...current.branchOperand, newOperand];
    } else {
      throw new Error(`Invalid path: Final key '${finalKey}' must be 'branchOperand'`);
    }
  
    return updatedExpression; // Return the updated expression
  }

  removeCWTBranchOperand(path:string[], operandIndex: number){
    this.removeCWTBranchOperandHelper(this.expression, path, operandIndex)
  }


  private removeCWTBranchOperandHelper(expression: any, path: string[], operandIndex: number): any {
    if (!Array.isArray(path) || path.length === 0) {
      throw new Error("Invalid path: Path must be a non-empty array");
    }
  
    if (typeof operandIndex !== "number" || operandIndex < 0) {
      throw new Error("Invalid operandIndex: Must be a non-negative integer");
    }
  
    // Create a deep copy of the expression for immutability
    const updatedExpression = { ...expression };
    let current = updatedExpression;
  
    // Traverse the path except for the last step
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
  
      if (key === "branchOperand") {
        if (!Array.isArray(current.branchOperand)) {
          throw new Error(`Invalid path: 'branchOperand' is not an array at path index ${i}`);
        }
        current = current.branchOperand;
      } else if (!isNaN(Number(key))) {
        const index = Number(key);
  
        if (index < 0 || index >= current.length) {
          throw new Error(`Invalid path: Index ${index} is out of bounds at path index ${i}`);
        }
        current = current[index];
      } else {
        if (!(key in current)) {
          throw new Error(`Invalid path: Unknown key '${key}' at path index ${i}`);
        }
        current = current[key];
      }
    }
  
    // Handle the final key
    const finalKey = path[path.length - 1];
  
    if (finalKey === "branchOperand") {
      if (!Array.isArray(current.branchOperand)) {
        throw new Error("Invalid path: 'branchOperand' is not an array");
      }
  
      if (operandIndex >= current.branchOperand.length) {
        throw new Error("Invalid operandIndex: Index is out of bounds");
      }
  
      // Remove the operand at the specified index
      current.branchOperand = current.branchOperand.filter((_:any, index:any) => index !== operandIndex);
    } else {
      throw new Error(`Invalid path: Final key '${finalKey}' must be 'branchOperand'`);
    }
  
    return updatedExpression; // Return the updated expression
  }
  
  
  private removeOperandHelper(expr: any, path: string[], operandIndex: number): any {
    if (!Array.isArray(path) || path.length === 0) {
      throw new Error("Invalid path: Path must be a non-empty array");
    }
  
    if (typeof operandIndex !== "number" || operandIndex < 0) {
      throw new Error("Invalid operand index: Must be a non-negative number");
    }
  
    // Create a deep copy to ensure immutability
    const updatedExpression = { ...expr };
    let current = updatedExpression;
  
    // Traverse the path except for the last step
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
  
      if (key === "operand") {
        if (!current.operand || typeof current.operand !== "object") {
          throw new Error(`Invalid path: 'operand' must be an object at path index ${i}`);
        }
        current = current.operand;
      } else if (key === "operands") {
        if (!Array.isArray(current.operands)) {
          throw new Error(`Invalid path: 'operands' is not an array at path index ${i}`);
        }
        current = current.operands;
      } else if (!isNaN(Number(key))) {
        const index = Number(key);
        if (!Array.isArray(current) || index < 0 || index >= current.length) {
          throw new Error(`Invalid path: Index ${index} is out of bounds at path index ${i}`);
        }
        current = current[index];
      } else {
        throw new Error(`Invalid path: Unknown key '${key}' at path index ${i}`);
      }
    }
  
    // Handle the final key
    const finalKey = path[path.length - 1];
  
    if (finalKey === "operands") {
      if (!current.operands || !Array.isArray(current.operands)) {
        throw new Error("Invalid path: Final 'operands' is not an array");
      }
  
      if (operandIndex >= current.operands.length) {
        throw new Error(`Invalid operand index: Index ${operandIndex} is out of bounds`);
      }
  
      // Remove the operand at the specified index
      current.operands.splice(operandIndex, 1);
    } else {
      throw new Error(`Invalid path: Final key '${finalKey}' must be 'operands'`);
    }
  
    return updatedExpression; // Return the updated expression
  }
  

  private removeRightOperandHelper(expr: any, path: string[], operandIndex: number): any {
    if (path.length === 0) {
      if (expr && typeof expr === "object") {
        if ("rightOperands" in expr) {
          // Ensure the operandIndex is valid
          if (operandIndex < 0 || operandIndex >= expr.rightOperands.length) {
            throw new Error("Invalid operand index for removal");
          }
  
          const updatedOperands = expr.rightOperands.filter((_:any, index:number) => index !== operandIndex);
  
          // Keep `rightOperands` if it still has elements
          if (updatedOperands.length > 0) {
            return {
              ...expr,
              rightOperands: updatedOperands,
            };
          }
  
          // If it becomes empty, clean up
          return {
            ...expr,
            rightOperands: undefined,
          };
        }
  
        if ("rightOperand" in expr) {
          // If `rightOperand` exists but you try to remove it by index
          if (operandIndex === 0) {
            const { rightOperand, ...rest } = expr; // Remove `rightOperand`
            return rest;
          } else {
            throw new Error("Invalid operand index for single rightOperand");
          }
        }
      }
  
      throw new Error("Invalid expression structure for removing right operand");
    }
  
    const [current, ...rest] = path;
  
    if (Array.isArray(expr) && !isNaN(parseInt(current))) {
      const index = parseInt(current);
      if (index >= expr.length) {
        throw new Error("Invalid path: index out of bounds");
      }
      const updatedItem = this.removeRightOperandHelper(expr[index], rest, operandIndex);
      return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
    } else if (typeof expr === "object" && expr !== null && current in expr) {
      // Update nested property in object
      return {
        ...expr,
        [current]: this.removeRightOperandHelper(expr[current], rest, operandIndex),
      };
    } else {
      throw new Error("Invalid path or expression type");
    }
  }
  


  private updateOperatorHelper(expr: any, path: string[], newOperator: string): any {
    if (path.length === 0) {
        // Check if it's a logical or comparison expression by looking for an `operator` property.
        if (expr && typeof expr === 'object' && 'operator' in expr) {
            return { ...expr, operator: newOperator };
        }
        throw new Error("Invalid expression structure for updating operator");
    }

    const [current, ...rest] = path;

    if (Array.isArray(expr) && !isNaN(parseInt(current))) {
        const index = parseInt(current);
        if (index >= expr.length) {
            throw new Error("Invalid path: index out of bounds");
        }
        const updatedItem = this.updateOperatorHelper(expr[index], rest, newOperator);
        return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
    } else if (typeof expr === 'object' && expr !== null && current in expr) {
        // Update nested property in object
        return {
            ...expr,
            [current]: this.updateOperatorHelper(expr[current], rest, newOperator),
        };
    } else {
        throw new Error("Invalid path or expression type");
    }
}

resetToRangeComparisonOperators(path: string[], obj: any) {
  this.expression = this.resetToRangeComparisonOperatorsHelper(this.expression, path, obj);
}
private resetToRangeComparisonOperatorsHelper(expr: any, path: string[], obj: any): any {
  if (path.length === 0) {
    if (expr && typeof expr === "object" && "rightOperands" in expr) {
      const rightOperands = Array.isArray(expr.rightOperands) ? expr.rightOperands : [];

      // Ensure rightOperands length is exactly 2
      if (rightOperands.length > 2) {
        // Pop excess elements
        while (rightOperands.length > 2) {
          rightOperands.pop();
        }
      } else if (rightOperands.length < 2) {
        // Push `obj` until length becomes 2
        while (rightOperands.length < 2) {
          rightOperands.push(obj);
        }
      }

      return {
        ...expr,
        rightOperands,
      };
    } else {
      throw new Error("Invalid expression structure for resetting range comparison operators");
    }
  }

  const [current, ...rest] = path;

  if (Array.isArray(expr) && !isNaN(parseInt(current))) {
    const index = parseInt(current);
    if (index >= expr.length) {
      throw new Error("Invalid path: index out of bounds");
    }
    const updatedItem = this.resetToRangeComparisonOperatorsHelper(expr[index], rest, obj);
    return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
  } else if (typeof expr === "object" && expr !== null && current in expr) {
    // Update nested property in object
    return {
      ...expr,
      [current]: this.resetToRangeComparisonOperatorsHelper(expr[current], rest, obj),
    };
  } else {
    throw new Error("Invalid path or expression type");
  }
}

resetToUnaryComparisonOperator(path: string[]) {
  this.expression = this.resetToUnaryComparisonOperatorHelper(this.expression, path);
}

toggleUnaryLogicalObject(path: string[]) {
  this.expression = this.toggleUnaryLogicalObjectHelper(this.expression, path);
}

private toggleUnaryLogicalObjectHelper(expr: any, path: string[]): any {
  if (path.length === 0) {
    if (expr && typeof expr === "object") {
      // If the object is already wrapped in NOT, unwrap it
      if (expr.operator === "NOT" && expr.operand) {
        return expr.operand;
      }
      
      // Otherwise, wrap it in NOT
      return {
        operator: "NOT",
        operand: expr,
      };
    }
    throw new Error("Invalid expression structure for toggling unary logical operator");
  }

  const [current, ...rest] = path;

  if (Array.isArray(expr) && !isNaN(parseInt(current))) {
    const index = parseInt(current);
    if (index >= expr.length) {
      throw new Error("Invalid path: index out of bounds");
    }
    return [
      ...expr.slice(0, index),
      this.toggleUnaryLogicalObjectHelper(expr[index], rest),
      ...expr.slice(index + 1),
    ];
  } else if (typeof expr === "object" && expr !== null && current in expr) {
    return {
      ...expr,
      [current]: this.toggleUnaryLogicalObjectHelper(expr[current], rest),
    };
  } else {
    throw new Error("Invalid path or expression type");
  }
}


private resetToUnaryComparisonOperatorHelper(expr: any, path: string[]): any {
  if (path.length === 0) {
    if (expr && typeof expr === "object") {
      // If the object already has only `operand`, leave it unchanged
      if ("operand" in expr && !("rightOperands" in expr) && !("rightOperand" in expr) && !("leftOperand" in expr)) {
        return expr;
      }

      // Check for and rename/remove relevant properties
      const { rightOperands, rightOperand, leftOperand, ...rest } = expr;

      if ("leftOperand" in expr) {
        // Rename `leftOperand` to `operand` and remove others
        return {
          ...rest,
          operand: leftOperand,
        };
      }

      // If only `rightOperands` or `rightOperand` exist without `leftOperand`, remove them
      if ("rightOperands" in expr || "rightOperand" in expr) {
        return {
          ...rest,
        };
      }

      throw new Error("Invalid expression structure: no valid operands found");
    }

    throw new Error("Invalid expression structure for resetting to unary comparison operator");
  }

  const [current, ...rest] = path;

  if (Array.isArray(expr) && !isNaN(parseInt(current))) {
    const index = parseInt(current);
    if (index >= expr.length) {
      throw new Error("Invalid path: index out of bounds");
    }
    const updatedItem = this.resetToUnaryComparisonOperatorHelper(expr[index], rest);
    return [...expr.slice(0, index), updatedItem, ...expr.slice(index + 1)];
  } else if (typeof expr === "object" && expr !== null && current in expr) {
    // Update nested property in object
    return {
      ...expr,
      [current]: this.resetToUnaryComparisonOperatorHelper(expr[current], rest),
    };
  } else {
    throw new Error("Invalid path or expression type");
  }
}

setArithmeticOperator(path: string[], operator: string) {
  const expression = this.getExpressionAtPath(path);
  if (expression && 'operator' in expression) {
    expression.operator = operator;
  }
}

}
// export as singleton
export const builderState = new BuilderState();